<template>
  <div
    :class="rootClasses"
    v-bind="$attrs"
  >
    <template
      v-for="(panel, idx) in panelNodes()"
    >
      <v
        :node="panel"
        :key="`panel-${idx}`"
        :props="{
          separatorSpace: (totalNodes - 1) * separatorSize,
          basis: panelSizes[idx],
        }"
      />
      <div
        v-if="idx < totalNodes - 1"
        :class="dividerClasses"
        :style="{
          [vertical ? 'height' : 'width']: `${separatorSize}px`,
        }"
        :key="`divider-${idx}`"
        ref="divider"
        role="separator"
        :data-test="`producer-splitter-${!vertical ? 'v' : 'h'}-${idx}`"
        @mousedown.stop.prevent="onMouseDown($event, idx)"
        @mouseup="onResizeEnd"
        @dblclick="onDoubleClick($event, idx)"
      />
    </template>
  </div>
</template>

<script>
import V from '@/js/components/VNodeRenderer.vue';

export default {
  name: 'Splitter',
  props: {
    vertical: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
    separatorSize: {
      type: Number,
      default: 2,
    },
  },
  components: {
    V,
  },
  data() {
    return {
      mouseMoveListener: null,
      mouseUpListener: null,
      wrapperSize: null,
      startPos: null,
      prevPanel: null,
      nextPanel: null,
      prevPanelSize: null,
      prevPanelSizeinPx: null,
      prevPanelProps: null,
      nextPanelSize: null,
      nextPanelSizeinPx: null,
      nextPanelProps: null,
      dragging: false,
      dataPanelSizes: this.value,
      initialCombinedPanelWidth: null,
    };
  },
  computed: {
    rootClasses() {
      return {
        splitter: true,
        'splitter--vertical': this.vertical,
      };
    },
    dividerClasses() {
      return {
        splitter__divider: true,
        'splitter__divider--vertical': this.vertical,
      };
    },
    totalNodes() {
      return this.panelNodes().length;
    },
    panelSizes: {
      get() {
        return this.dataPanelSizes;
      },
      set(val) {
        this.dataPanelSizes = val;
      },
    },
  },
  watch: {
    value(val) {
      this.dataPanelSizes = val;
    },
    panelSizes(val) {
      if (!this.dragging) {
        this.$emit('input', val);
      }
    },
  },
  mounted() {
    if (!this.value.length) {
      this.panelSizes = Array(this.totalNodes).fill(100 / this.totalNodes);
    }
  },
  beforeDestroy() {
    this.unbindMouseListeners(); // just in case
  },
  methods: {
    panelNodes() {
      // Can't use computed property because slots aren't reactive
      return this.$slots.default.filter((node) => node?.tag?.endsWith('SplitterPanel'));
    },
    validateResize(prevPanel, nextPanel) {
      const prevFixedWidth = !!this.prevPanelProps.fixedWidth;
      const nextFixedWidth = !!this.nextPanelProps.fixedWidth;
      let prevMinSize;
      let nextMinSize;

      if (this.vertical) {
        prevMinSize = this.prevPanelProps.minHeight || 0;
        nextMinSize = this.nextPanelProps.minHeight || 0;
      } else {
        prevMinSize = this.prevPanelProps.minWidth || 0;
        nextMinSize = this.nextPanelProps.minWidth || 0;
      }

      if (prevFixedWidth) {
        if (prevPanel < prevMinSize) return false;
      } else if (prevPanel > 100 || prevPanel < 0) {
        return false;
      }

      if (nextFixedWidth) {
        if (nextPanel < nextMinSize) return false;
      } else if (nextPanel > 100 || nextPanel < 0) {
        return false;
      }

      return true;
    },
    convertToPanelPct(panelSize) {
      return +parseFloat(100 * (panelSize / this.wrapperSize)).toFixed(4);
    },
    onResizeStart(e, idx) {
      this.dragging = true;
      const divider = this.$refs.divider[idx];
      this.wrapperSize = this.vertical ? this.$el.clientHeight : this.$el.clientWidth;
      this.startPos = this.vertical ? e.clientY : e.clientX;
      this.prevPanel = divider.previousElementSibling;
      this.nextPanel = divider.nextElementSibling;

      // get the props of the panels for checks
      this.prevPanelProps = this.panelNodes()[idx].componentOptions.propsData;
      this.nextPanelProps = this.panelNodes()[idx + 1].componentOptions.propsData;

      this.prevPanelSizeinPx = this.vertical ? this.prevPanel.clientHeight : this.prevPanel.clientWidth;
      this.nextPanelSizeinPx = this.vertical ? this.nextPanel.clientHeight : this.nextPanel.clientWidth;
      this.initialCombinedPanelWidth = this.prevPanelSizeinPx + this.nextPanelSizeinPx;
    },
    onResize(e, idx) {
      let newPosPx;
      let minPrevPanelSize;
      let minNextPanelSize;
      const prevFixedWidth = !!this.prevPanelProps.fixedWidth;
      const nextFixedWidth = !!this.nextPanelProps.fixedWidth;

      if (this.vertical) {
        newPosPx = e.clientY - this.startPos;
        minPrevPanelSize = this.prevPanelProps.minHeight || 0;
        minNextPanelSize = this.nextPanelProps.minHeight || 0;
      }
      else {
        newPosPx = e.clientX - this.startPos;
        minPrevPanelSize = this.prevPanelProps.minWidth || 0;
        minNextPanelSize = this.nextPanelProps.minWidth || 0;
      }

      const newPrevPanelPx = this.prevPanelSizeinPx + newPosPx;
      const newNextPanelPx = this.nextPanelSizeinPx - newPosPx;
      let newPrevPanelSize = newPrevPanelPx;
      let newNextPanelSize = newNextPanelPx;

      // auto collapse previous below a certain size
      if (this.prevPanelProps.collapseBelow) {
        const buffer = this.prevPanelProps.collapseBuffer || 0;
        const collapseWidth = this.prevPanelProps.collapseBelow;

        if (newPrevPanelPx < collapseWidth) {
          if (newPrevPanelPx < (collapseWidth - buffer)) {
            // collapse
            newNextPanelSize = newNextPanelPx + newPrevPanelPx - minPrevPanelSize - this.separatorSize;
            newPrevPanelSize = minPrevPanelSize;
          } else {

            // "freeze" the panel size until the buffer is exceeded
            newPrevPanelSize = collapseWidth;
            newNextPanelSize = this.initialCombinedPanelWidth - collapseWidth < 0 ? this.nextPanelSizeinPx : this.initialCombinedPanelWidth - collapseWidth;
          }
        }
      }

      // auto collapse next below a certain size
      if (this.nextPanelProps.collapseBelow) {
        const buffer = this.nextPanelProps.collapseBuffer || 0;
        const collapseWidth = this.nextPanelProps.collapseBelow;

        if (newNextPanelPx < collapseWidth) {
          if (newNextPanelPx < (collapseWidth - buffer)) {
            // collapse
            newPrevPanelSize = newPrevPanelPx + newNextPanelPx - minNextPanelSize - this.separatorSize;
            newNextPanelSize = minNextPanelSize;
          } else {
            // "freeze" the panel size until the buffer is exceeded
            newNextPanelSize = collapseWidth;
            newPrevPanelSize = this.initialCombinedPanelWidth - collapseWidth;
          }
        }
      }

      if (!prevFixedWidth) newPrevPanelSize = this.convertToPanelPct(newPrevPanelSize);
      if (!nextFixedWidth) newNextPanelSize = this.convertToPanelPct(newNextPanelSize);

      // check if the new sizes are valid and set them
      if (this.validateResize(newPrevPanelSize, newNextPanelSize)) {
        this.panelSizes = this.panelSizes.map((size, i) => {
          if (i === idx) return newPrevPanelSize;
          if (i === idx + 1) return newNextPanelSize;
          return size;
        });
      }
    },
    onResizeEnd(e) {
      this.dragging = false;
      this.unbindMouseListeners();
      if (this.value.length) {
        this.$emit('input', this.panelSizes);
      }

      this.$emit('resize-end', e);
    },
    unbindMouseListeners() {
      document.removeEventListener('mousemove', this.mouseMoveListener);
      this.mouseMoveListener = null;

      document.removeEventListener('mouseup', this.mouseUpListener);
      this.mouseUpListener = null;
    },
    onMouseDown(e, idx) {
      this.onResizeStart(e, idx);
      this.mouseMoveListener = (e) => this.onResize(e, idx);
      document.addEventListener('mousemove', this.mouseMoveListener);

      this.mouseUpListener = (e) => {
        this.onResizeEnd(e);
      };

      document.addEventListener('mouseup', this.mouseUpListener);
    },

    onDoubleClick(e, idx) {
      const prevProps = this.panelNodes()[idx].componentOptions.propsData;

      const {
        fixedWidth,
        defaultWidth = 300,
      } = prevProps;

      if (fixedWidth) {
        const delta = this.panelSizes[idx] - defaultWidth;
        const nextPanelSize = this.panelSizes[idx + 1] + this.convertToPanelPct(delta);

        // If the panel is fixed and has a default width, just reset to that value on double click.
        this.panelSizes = this.panelSizes.map((size, i) => {
          if (i === idx) {
            return defaultWidth;
          }

          if (i === idx + 1) {
            return nextPanelSize;
          }

          return size;
        });

        return;
      }

      // split evenly between two panels
      const totalSize = this.panelSizes[idx] + this.panelSizes[idx + 1];
      const newPanelSize = totalSize / 2;

      this.panelSizes = this.panelSizes.map((size, i) => {
        if (i === idx) {
          return newPanelSize;
        }

        if (i === idx + 1) {
          return newPanelSize;
        }

        return size;
      });
    },
  },
};
</script>

<style lang="scss" scoped>

.splitter {
  display: flex;

  &--vertical {
    flex-direction: column;
  }

  &__divider {
    background-color: variables.$color-02dp;
    flex-grow: 0;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: col-resize;
    position: relative;
    transition: background-color 150ms;

    // ::before is to increase the hit area of the divider
    // ::after is to show the drag handle
    &::before {
      content: '';
      height: 100%;
      width: 16px;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 1;
      background-color: transparent;
    }

    &:hover,
    &:active {
      background-color: #0092A5;
    }

    &--vertical {
      width: 100%;
      cursor: row-resize;

      &::before {
        width: 100%;
        height: 16px;
      }

      &::after {
        width: 45px !important;
        height: 8px !important;
      }
    }
  }
}
</style>
